// Summer Volleyball dual-team squad flyer — Heraldic Premium.
//
// Rebuilt to match the cricket squad flyer (FlyerPoster) visual
// language: luxury navy ground, metallic gold gradient wordmark,
// ornamental gold flourishes, parchment player ribbons with gold-
// ringed pop-out avatars, red volunteer boxes with gold trim.
//
// The main structural difference vs. the single-team cricket poster
// is the dual roster — Pitt Gabruz + Pitt Baazigars side by side.
// Everything else reuses the shared .fp-* CSS classes from the
// cricket flyer so the two look like a matched set.

import React from "react";
import {
  CalendarDays, Clock, MapPin, Droplet, Banana, Users as UsersIcon
} from "lucide-react";
import Avatar from "../Avatar";
import { formatDateTemplate, formatTimeTemplate, splitVenue } from "./flyerHelpers";
import { Flourish, InfoDivider } from "./FlyerPoster";

function fallbackInitials(name) {
  const parts = String(name || "").trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  const first = parts[0][0] || "";
  const last  = parts.length > 1 ? (parts[parts.length - 1][0] || "") : (parts[0][1] || "");
  return (first + last).toUpperCase();
}

// Volleyball ribbon — same structure as the cricket one, but no
// skill pills (volleyball players don't carry the cricket skill
// codes in this app) and no wicket-keeper badge.
function VBPlayerRibbon({ player, isCaptain }) {
  return (
    <div className="fp-ribbon-wrap">
      <div className="fp-ribbon-avatar-wrap">
        <div className="fp-ribbon-avatar">
          {player.id && player.avatarUpdatedAt ? (
            <Avatar
              member={{ id: player.id, name: player.name, avatarUpdatedAt: player.avatarUpdatedAt }}
              size={64}
              style={{ width: "100%", height: "100%", borderRadius: "50%", background: "transparent" }}
            />
          ) : (
            <span className="fp-ribbon-initials">{fallbackInitials(player.name)}</span>
          )}
        </div>
        {isCaptain && (
          <span className="fp-ribbon-badge fp-ribbon-badge--c">C</span>
        )}
      </div>
      <article className={`fp-ribbon${isCaptain ? " fp-ribbon--c" : ""}`}>
        <div className="fp-ribbon-name">{player.name}</div>
      </article>
    </div>
  );
}

// Roster column with a compact team header on top.
function VBRoster({ label, players, captainId }) {
  const sorted = [...(players || [])].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
  return (
    <section className="fp-vb-rosterCol">
      <h3 className="fp-vb-rosterLabel">{label}</h3>
      <div className="fp-roster">
        {sorted.length === 0 && (
          <p className="fp-roster-empty">No players named yet.</p>
        )}
        {sorted.map((p, i) => (
          <VBPlayerRibbon
            key={p.id || `${label}-${i}`}
            player={p}
            isCaptain={!!p.id && p.id === captainId}
          />
        ))}
      </div>
    </section>
  );
}

export function SummerVolleyballFlyer({
  match, reportingTime, venue,
  startingPlayers, teamBStartingPlayers,
  benchPlayers, teamBBenchPlayers,
  snacksVolunteer, alcoholVolunteer,
  captainPickId = null
}) {
  const { short: venueShort } = splitVenue(venue);
  const dateStr    = formatDateTemplate(match?.date)     || "TBD";
  const reportStr  = formatTimeTemplate(reportingTime)   || "TBD";
  const groundStr  = venueShort || "TBD";
  const showVolunteers = !!(snacksVolunteer || alcoholVolunteer);

  return (
    <div className="poster fp fp-heraldic fp--volleyball">

      {/* ── Topline ── */}
      <div className="fp-topline">
        <Flourish/>
        <span className="fp-topline-txt">Summer Volleyball Championship</span>
        <Flourish mirror/>
      </div>

      {/* ── Wordmark ── */}
      <h1 className="fp-brand">
        <span>GABRUZ</span>
        <span>UNIVERSE</span>
      </h1>

      {/* ── Team-vs-team hero strip (replaces the cricket team-line) ── */}
      <div className="fp-vb-hero">
        <div className="fp-vb-heroTeam">
          <small>TEAM A</small>
          <b>PITT GABRUZ</b>
        </div>
        <div className="fp-vb-heroVs">
          <span className="fp-vb-heroVs-rule"/>
          <em>VS</em>
          <span className="fp-vb-heroVs-rule"/>
        </div>
        <div className="fp-vb-heroTeam">
          <small>TEAM B</small>
          <b>PITT BAAZIGARS</b>
        </div>
      </div>

      {/* ── Match info row (Match Day · Reporting Time · Court) ── */}
      <div className="fp-info">
        <div className="fp-info-item">
          <span className="fp-info-icon"><CalendarDays size={18}/></span>
          <div>
            <small>MATCH DAY</small>
            <b>{dateStr}</b>
          </div>
        </div>
        <InfoDivider/>
        <div className="fp-info-item">
          <span className="fp-info-icon"><Clock size={18}/></span>
          <div>
            <small>REPORTING TIME</small>
            <b>{reportStr}</b>
          </div>
        </div>
        <InfoDivider/>
        <div className="fp-info-item">
          <span className="fp-info-icon"><MapPin size={18}/></span>
          <div>
            <small>COURT</small>
            <b>{groundStr}</b>
          </div>
        </div>
      </div>

      {/* ── Ornamental section divider ── */}
      <div className="fp-section-divider">
        <Flourish w={80}/>
        <span className="fp-section-divider-txt">Team Rosters</span>
        <Flourish w={80} mirror/>
      </div>

      {/* ── Two rosters side by side ── */}
      <div className="fp-vb-rosters">
        <VBRoster
          label="PITT GABRUZ"
          players={startingPlayers}
          captainId={captainPickId}
        />
        <VBRoster
          label="PITT BAAZIGARS"
          players={teamBStartingPlayers}
          captainId={null}
        />
      </div>

      {/* ── Volunteers (Snacks / Beverages for volleyball) ── */}
      {showVolunteers && (
        <div className="fp-vol">
          <div className="fp-vol-item">
            <span className="fp-vol-item-icon"><Droplet size={22}/></span>
            <div className="fp-vol-item-body">
              <small>SNACKS</small>
              <div className="fp-vol-item-role">CHIPS / FRUIT</div>
              <b>{snacksVolunteer || "TBD"}</b>
            </div>
          </div>
          <div className="fp-vol-item">
            <span className="fp-vol-item-icon"><Banana size={22}/></span>
            <div className="fp-vol-item-body">
              <small>BEVERAGES</small>
              <div className="fp-vol-item-role">DRINKS / GATORADE</div>
              <b>{alcoholVolunteer || "TBD"}</b>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
